/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountService from 'cio/services/account';
import EnvironmentService from 'cio/services/environment';
import { VerificationState } from 'cio/models/account';
import { DeliverySettings } from 'cio/models/environment';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import { task, timeout } from 'ember-concurrency';

interface TestDeliveryModePopoverSignature {
  Blocks: {
    default: [];
  };
  Args: {
    popupClassNames?: string;
    allowOpen?: boolean;
  };
}

export default class TestDeliveryModePopover extends Component<TestDeliveryModePopoverSignature> {
  @service features!: FeaturesService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  get isEnabled() {
    if (this.args.allowOpen === false) {
      return false;
    }
    return this.features.isEnabled('test_delivery_mode') && this.isDeliverySettingsSendToOne;
  }

  get popupClassNames() {
    return this.args.popupClassNames ?? '';
  }

  // Only track popover opened events if it's actually kept open
  // (avoids tracking when the user hovers briefly over the trigger)
  trackPopoverOpened = task({ restartable: true }, async () => {
    await timeout(750);

    // Since the popover component can control its own visibility,
    // we can't rely on the close function to track when the popover
    // is actually closed. Instead, we check if the popover content
    // is still in the DOM.
    if (document.querySelector('.test-delivery-mode-popover-content') == null) {
      return;
    }

    this.analytics.track('test_delivery_mode', 'popover_opened', { routeName: this.router.currentRoute?.name });
  });

  @action
  openPopover(openPopoverFn: () => unknown) {
    if (!this.isEnabled) {
      return;
    }
    openPopoverFn();

    this.trackPopoverOpened.perform();
  }

  @action
  handleCtaClick(instantClosePopoverFn: () => unknown, event: MouseEvent) {
    let element = event.currentTarget as HTMLElement;
    this.analytics.track('test_delivery_mode', 'popover_cta_clicked', {
      routeName: this.router.currentRoute?.name,
      actionName: element.textContent?.trim(),
    });

    // TODO: Weird issue: immediately closing the popover when clicking on a
    // LinkTo inside it causes the page to reload while navigating to the
    // route, instead of navigating smoothly.
    // Adding this very short timeout fixes the issue.
    //
    // Couldn't determine the cause, maybe something to do with the
    // LinkTo component being destroyed before the click event is finished?

    setTimeout(() => {
      instantClosePopoverFn();
    }, 1);
  }

  @action
  closePopover(closePopoverFn: () => unknown) {
    if (!this.isEnabled) {
      return;
    }
    closePopoverFn();
  }

  get isDeliverySettingsSendToOne() {
    return this.environment.current?.deliverySettings === DeliverySettings.SendToOne;
  }

  get testDeliveryEmailAddress() {
    return this.environment.current.deliveryEmail;
  }

  get isAccountVerified() {
    return this.account.current?.verificationState === VerificationState.Approved;
  }

  get isFromAddressSetUp() {
    return this.environment.current?.identities.length > 0 ?? false;
  }

  get verifiedDomains() {
    return this.environment.domains?.filter((domain) => domain.verified) ?? [];
  }

  get domainCount() {
    return this.verifiedDomains.length;
  }

  get isSendingDomainSetUp() {
    return this.domainCount > 0;
  }
}
